import { Entry } from '~/src/design/template/option';
import { ColumnProp } from './types';
import {
	Option,
	Template,
	Variable
} from '~/src/design/template';
import {
	isSelectedOption,
	isSelectedVariable,
} from '~/src/design/template/edit';
import { NamedLeaf } from '~/src/model/NamedTree';

const getValueFromTemplate = (
	label: string,
	templateValues: ((Variable | Option) & { Type: string })[]
): string | number => {
	if (templateValues) {
		const searchLabel = templateValues.find(
			(item: (Variable | Option) & { Type: string }) => item.Label === label
		);
		if (searchLabel) {
			switch (searchLabel.Type) {
			case 'Opts':
				return (
					(searchLabel.Range as Entry<string>[]).find(
						(r) => r.Label === searchLabel.Value
					)?.Names?.eng ?? ''
				);
			case 'Vars':
				return searchLabel.Value;
			}
		}
	}
	return '';
};

export const columns: ColumnProp[] = [
	{
		id: 'measurements',
		width: '141px',
		label: 'Measurements',
		render: (_node, _quote, tValues) => {
			const shape = getValueFromTemplate('Shape', tValues);

			if (['Circle', 'Half Circle'].includes(shape as string)) {
				const diameter = getValueFromTemplate('diameter', tValues);
				if (diameter) {
					return `${diameter}"`;
				}
			} else if (['Square', 'Octagon', 'Quarter Circle', 'Equilateral Pentagon', 'Right Pentagon', 'Right Isosceles Triangle'].includes(shape as string)) {
				const sideLength = getValueFromTemplate('side', tValues);
				if (sideLength) {
					return `${sideLength}"`;
				}
			} else if (['Trapzoid', 'Parallelogram'].includes(shape as string)) {
				const width = getValueFromTemplate('width', tValues);
				const height = getValueFromTemplate('height', tValues);
				const skew = getValueFromTemplate('skew', tValues);

				return `${width}"x${height}", ${skew}"`;
			} else {
				const width = getValueFromTemplate('width', tValues);
				const height = getValueFromTemplate('height', tValues);
				if (width || height) {
					return `${width}"x${height}"`;
				}
			}
			return '-';
		},
	},
	{
		id: 'shape',
		width: '87px',
		label: 'Shape',
		render: (_node, _quote, tValues) => getValueFromTemplate('Shape', tValues),
	},
	{
		id: 'edge-finish',
		width: '120px',
		label: 'Edge Finish',
		render: (_node, _quote, tValues) => getValueFromTemplate('Edge', tValues),
	},
	{
		id: 'tempered',
		width: '115px',
		label: 'Tempered',
		render: (_node, _quote, tValues) => {
			const tempered = getValueFromTemplate('TemperMark', tValues);
			return tempered;
		},
	},
	{
		id: 'thickness',
		width: '116px',
		label: 'Thickness',
		render: (_node, _quote, tValues) => {
			const thickness = getValueFromTemplate('Thickness', tValues);
			return thickness;
		},
	},
	{
		id: 'corners',
		width: '98px',
		label: 'Corners',
		render: (node, _quote, tValues) => {
			const formatCornerValue = (cornerIndex: number) => {
				const cornerValue = tValues.find(
					(item: Option) => item.Label === `Corner ${cornerIndex}`
				);
				const CornerLabels = ['BL', 'BR', 'TR', 'TL'];
				switch (cornerValue.Value) {
				case `none${cornerIndex}`:
					return 'None';
				case `eased${cornerIndex}`:
					return `${CornerLabels[cornerIndex]}: E`;
				case `clipped${cornerIndex}`:
					return `${CornerLabels[cornerIndex]}: C${getValueFromTemplate(
						`corner${cornerIndex}-clip`,
						tValues
					)}"`;
				case `rounded${cornerIndex}`:
					return `${CornerLabels[cornerIndex]}: R${getValueFromTemplate(
						`corner${cornerIndex}-radius`,
						tValues
					)}"`;
				default:
					return '';
				}
			};

			const cornerInfo = [3, 2, 1, 0].map((index) => {
				const cornerOpt = tValues.find((opt: Option) => opt.Label === `Corner ${index}`);
				const isValid = isSelectedOption((node as NamedLeaf<unknown>).Item as Template)(cornerOpt);

				if (isValid) {
					return formatCornerValue(index);
				}

				return null;
			}).filter((cor) => !!cor);

			const isInvalidCornerInfo = cornerInfo.length === 0;
			if (isInvalidCornerInfo) {
				return (
					<div className="tree-node-column__corner">
						<p>-</p>
					</div>
				);
			}

			const isEveryCornerNone = cornerInfo.every((cor) => cor === 'None');
			if (isEveryCornerNone) {
				return (
					<div className="tree-node-column__corner">
						<p>None</p>
					</div>
				);
			}

			return (
				<div className="tree-node-column__corner">
					{cornerInfo.filter((cor) => cor !== 'None').map((cor, index) => (
						<p key={index}>{cor}</p>
					))}
				</div>
			);
		},
	},
	{
		id: 'diameter',
		width: '104px',
		label: 'Diameter',
		render: (node, _quote, tValues) => {
			const diameterOpt = tValues.find((opt: Option) => opt.Label === 'diameter');
			const isValid = isSelectedVariable((node as NamedLeaf<unknown>).Item as Template)(diameterOpt);

			if (isValid) {
				return getValueFromTemplate('diameter', tValues);
			}
			return '';
		},
	},
	{
		id: 'sku',
		width: '70px',
		label: 'SKU',
		render: () => '-',
	},
	{
		id: 'price',
		width: '90px',
		label: 'Price',
		align: 'right',
		render: (_node, quote) => `$ ${quote?.Total.USD}`,
	},
];
